import React, {useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {Box, CircularProgress, Stack, TextareaAutosize} from '@mui/material';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import RadialMenu, {RadialMenuItem} from '@/components/radial-menu';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import {uploadMessageAttachment} from '@/api/messages';
import {SendMessagePayload, UploadedAttachment} from '@/api/messages/types';
import {ATTACHMENT_ACCEPT_ATTR} from '@/api/messages/constants';
import validateAttachment from './validateAttachment';
import styles from './MessageComposer.module.scss';

interface Props {
  onSend: (payload: SendMessagePayload) => Promise<unknown>;
  allowAttachments?: boolean;
  disabled?: boolean;
}

interface StagedAttachment {
  id: string;
  fileName: string;
  status: 'uploading' | 'uploaded';
  uploaded?: UploadedAttachment;
}

const EMOJI_OPTIONS = ['👍', '👀', '🚩', '✅', '🔥', '😊'] as const;
const INITIAL_ANGLE = 200;
const ANGLE_OFFSET = 35;
const EMOJI_ITEMS: RadialMenuItem[] = EMOJI_OPTIONS.map((emoji, index) => ({
  id: emoji,
  angle: INITIAL_ANGLE + index * ANGLE_OFFSET,
  content: (
    <Box component="span" sx={{fontSize: '20px', lineHeight: 1}}>
      {emoji}
    </Box>
  ),
}));

export default function MessageComposer({
  onSend,
  allowAttachments = false,
  disabled = false,
}: Props) {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isUploading = attachments.some((a) => a.status === 'uploading');
  const canSend =
    !disabled && !sending && !isUploading && text.trim().length > 0;

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (inputRef.current) inputRef.current.value = '';
    picked.forEach((file) => {
      const result = validateAttachment(file);
      if (!result.valid) {
        raiseFlashMessage({status: 'error', message: result.reason});
        return;
      }
      const id = uuidv4();
      setAttachments((current) => [
        ...current,
        {id, fileName: file.name, status: 'uploading'},
      ]);
      uploadMessageAttachment(file)
        .then((uploaded) => {
          setAttachments((current) =>
            current.map((a) =>
              a.id === id ? {...a, status: 'uploaded', uploaded} : a
            )
          );
        })
        .catch(() => {
          raiseFlashMessage({
            status: 'error',
            message: `Couldn’t upload ${file.name}.`,
          });
          setAttachments((current) => current.filter((a) => a.id !== id));
        });
    });
  };

  const removeAttachment = (id: string) => {
    setAttachments((current) => current.filter((a) => a.id !== id));
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const uploaded = attachments
        .filter((a) => a.uploaded)
        .map((a) => a.uploaded as UploadedAttachment);
      await onSend({body: text.trim(), attachments: uploaded});
      setText('');
      setAttachments([]);
    } catch (e) {
      raiseFlashMessage({
        status: 'error',
        message: 'Couldn’t send your message. Please try again.',
      });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack
      direction="column"
      spacing={0.5}
      className={styles.composer}
      data-testid="message-composer"
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        className={styles.inputRow}
      >
        <TextareaAutosize
          className={styles.textarea}
          data-testid="message-input"
          placeholder="Write a message…"
          minRows={1}
          maxRows={5}
          value={text}
          disabled={disabled || sending}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <RadialMenu
          interactionMode="click"
          items={EMOJI_ITEMS}
          selectedIds={[]}
          radius={60}
          offsetRotation={-45}
          onOptionClick={(emoji) => setText((current) => current + emoji)}
          trigger={
            <Button
              size="small"
              appearance="neutral"
              mode="plain"
              className={styles.emojiButton}
              aria-label="Add emoji"
              disabled={disabled || sending}
            >
              😊
            </Button>
          }
        />
        {allowAttachments && (
          <>
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple
              accept={ATTACHMENT_ACCEPT_ATTR}
              data-testid="attachment-input"
              onChange={onPick}
            />
            <Button
              size="small"
              appearance="neutral"
              mode="plain"
              className={styles.attachButton}
              aria-label="Attach a file"
              data-testid="attachment-button"
              disabled={disabled || sending}
              onClick={() => inputRef.current?.click()}
            >
              <Icon
                size="small"
                appearance="neutral"
                name="Navigation-Attach"
              />
            </Button>
          </>
        )}
        <Button
          size="small"
          appearance="brand"
          mode="filled"
          className={styles.sendButton}
          testID="send-message"
          aria-label="Send"
          disabled={!canSend}
          onClick={handleSend}
        >
          <Icon size="small" appearance="white" name="Navigation-Share" />
        </Button>
      </Stack>

      {attachments.length > 0 && (
        <Stack
          direction="column"
          spacing={0.5}
          className={styles.pendingAttachments}
        >
          {attachments.map((attachment) => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              className={styles.pendingAttachmentRow}
              key={attachment.id}
            >
              {attachment.status === 'uploading' ? (
                <CircularProgress size={12} />
              ) : (
                <Icon
                  size="small"
                  appearance="neutral"
                  name="Navigation-Attach"
                />
              )}
              <Body size="xs" className={styles.pendingName}>
                {attachment.status === 'uploading'
                  ? `Uploading ${attachment.fileName}…`
                  : attachment.fileName}
              </Body>
              <Button
                size="small"
                appearance="neutral"
                mode="plain"
                className={styles.removeFile}
                aria-label={`Remove ${attachment.fileName}`}
                onClick={() => removeAttachment(attachment.id)}
              >
                <Icon
                  size="small"
                  appearance="neutral"
                  name="Actions-Close-Normal"
                />
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
